import { useState } from 'react';
import { regionOptions, yearRangeOptions } from '@/types/movie';

export type SortKey = 'hot' | 'latest' | 'rating';

interface FilterPanelProps {
  genreOptions: string[];
  genreSelected: string[];
  onToggleGenre: (g: string) => void;
  regionSelected: string[];
  onToggleRegion: (r: string) => void;
  yearSelected: string[];
  onToggleYear: (label: string) => void;
  sort: SortKey;
  onSortChange: (s: SortKey) => void;
  onReset: () => void;
  showRegions?: boolean;
}

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'hot', label: '热门' },
  { key: 'latest', label: '最新' },
  { key: 'rating', label: '评分' },
];

export default function FilterPanel({
  genreOptions,
  genreSelected,
  onToggleGenre,
  regionSelected,
  onToggleRegion,
  yearSelected,
  onToggleYear,
  sort,
  onSortChange,
  onReset,
  showRegions = true,
}: FilterPanelProps) {
  const [open, setOpen] = useState(false);
  const hasActive = genreSelected.length > 0 || regionSelected.length > 0 || yearSelected.length > 0;

  const chip = (active: boolean) =>
    `h-8 px-3.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
      active
        ? 'bg-primary-500 text-background-50'
        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
    }`;

  const row = (label: string, children: React.ReactNode) => (
    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-b-0">
      <span className="sm:w-16 shrink-0 pt-1 text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex-1 flex flex-wrap gap-2">{children}</div>
    </div>
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      {/* Mobile toggle */}
      <div className="sm:hidden flex items-center justify-between px-4 py-3">
        <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2 text-sm font-medium text-foreground-800 cursor-pointer">
          <i className="ri-equalizer-line text-base text-primary-600" />
          筛选与排序
          {hasActive && <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />}
        </button>
        <i className={open ? 'ri-arrow-up-s-line text-foreground-500' : 'ri-arrow-down-s-line text-foreground-500'} />
      </div>

      <div className={`px-4 md:px-5 py-3 ${open ? 'block' : 'hidden'} sm:block`}>
        {row('排序', sortOptions.map((s) => (
          <button key={s.key} type="button" onClick={() => onSortChange(s.key)} className={chip(sort === s.key)}>{s.label}</button>
        )))}
        {row('类型', genreOptions.map((g) => (
          <button key={g} type="button" onClick={() => onToggleGenre(g)} className={chip(genreSelected.includes(g))}>{g}</button>
        )))}
        {showRegions && row('地区', regionOptions.map((r) => (
          <button key={r} type="button" onClick={() => onToggleRegion(r)} className={chip(regionSelected.includes(r))}>{r}</button>
        )))}
        {row('年份', yearRangeOptions.map((y) => (
          <button key={y.label} type="button" onClick={() => onToggleYear(y.label)} className={chip(yearSelected.includes(y.label))}>{y.label}</button>
        )))}
        {hasActive && (
          <div className="pt-3">
            <button type="button" onClick={onReset} className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-background-300 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}